// Onglets de scope d'un index (« Tous / Locataires / Propriétaires / Pros de l'immo… »).
//
// Le clic recharge la frame de l'index d'un bloc — en-tête, barre de scopes, tableau — et,
// contrairement à un onglet de la sidebar, il n'y a PAS de coquille intermédiaire : la
// réponse est directement la liste. Rien ne bougeait donc à l'écran pendant tout
// l'aller-retour, pas même l'onglet cliqué. Mesuré sur l'index Utilisateurs (jeu de test,
// 40 738 utilisateurs, 25 lignes rendues d'un coup puisque `lazy_previews` est faux en vue
// tableau) : 217 à 421 ms de rendu serveur par changement de scope, réseau non compris.
//
// Même remède que la recherche et que les filtres d'en-tête : le corps du tableau passe en
// barres dès le clic (`skeletonizeSearchResults`), et l'onglet cliqué prend l'état actif.
//
// Rien à défaire : la région squelettisée est exactement celle que Turbo remplace, et un
// échec de chargement la remplace lui aussi (`showTurboFrameLoadError`, cf. entrypoints/common).
// Les index en lignes ou en cartes n'ont pas de tableau à squelettiser ; leurs aperçus portent
// `turbo-blur`, que le `[busy]` posé par Turbo sur la frame floute déjà (cf. config/utils).

import { clearSearchResultsSkeleton, skeletonizeSearchResults } from '../entrypoints/common';

const SCOPE_LINK_SELECTOR = 'a[data-index-scope]';

// La région que Turbo va remplacer, résolue comme le fait FrameController#findFrameElement :
// la frame désignée par `data-turbo-frame` si c'en est une, sinon la frame qui contient le
// lien. Sur un index en tableau, `…_search_results` est un <div> — une turbo-frame autour
// d'un <table> est déplacée par le parseur HTML — donc c'est la frame de l'index entier qui
// est rechargée, et c'est bien là qu'il faut aller chercher le tableau.
const regionForScopeLink = (link: HTMLElement): HTMLElement | null => {
  const frameId = link.dataset.turboFrame;
  const targeted = frameId ? document.getElementById(frameId) : null;
  if (targeted?.tagName === 'TURBO-FRAME') return targeted;

  return link.closest('turbo-frame');
};

// Même pari que l'onglet actif de la sidebar : l'état est posé tout de suite, et c'est la
// réponse du serveur qui tranche — la barre de scopes fait partie de la région remplacée.
// Il n'y a donc rien à rattraper si le clic ne mène nulle part : la barre repart avec la
// région à chaque rendu, réussi comme en erreur.
const markScopeActive = (link: HTMLElement) => {
  const bar = link.closest('ul');
  if (!bar) return;

  bar.querySelectorAll<HTMLElement>(SCOPE_LINK_SELECTOR).forEach((tab) => {
    tab.classList.toggle('active', tab === link);
  });
};

export function initializeIndexScopeSkeleton() {
  if ((window as any).__indexScopeSkeletonInitialized) return;
  (window as any).__indexScopeSkeletonInitialized = true;

  // `turbo:click` plutôt qu'un écouteur de clic : Turbo l'émet pour TOUS les liens qu'il
  // prend en charge, y compris ceux qui se chargent dans une frame. C'est justement
  // l'intercepteur de la frame qui l'annule ensuite pour piloter la navigation lui-même —
  // d'où l'absence de garde sur `defaultPrevented`, qui ne dirait rien de la navigation.
  document.addEventListener('turbo:click', (event) => {
    const link = (event.target as Element | null)?.closest<HTMLElement>(SCOPE_LINK_SELECTOR);
    if (!link) return;

    markScopeActive(link);

    const region = regionForScopeLink(link);
    if (region) skeletonizeSearchResults(region);
  });

  // Le contenu de la région est remplacé, mais la frame qui la porte, elle, survit au rendu :
  // sans ça elle resterait annoncée occupée (`aria-busy`) et marquée squelettisée. Turbo lève
  // son propre `[busy]` au même instant. Les barres, elles, sont parties avec l'ancien corps
  // de tableau — il n'y a rien à reconstruire.
  document.addEventListener('turbo:frame-load', (event) => {
    clearSearchResultsSkeleton(event.target as Element | null);
  });
}
